// FormSettingsReportLayout — the "Report layout" tab of the Form settings
// drawer: text direction / page size / photo-resolution selects, a thumbnail-
// size card picker, and one list of report toggles — the footer-text field
// renders inside that list, right after the row that owns it. Self-
// contained local state, seeded from defaults; saves via
// PATCH /forms/update-report-layout/:id through the drawer's imperative handle.

import { Fragment, forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, SelectDropdown, Switch, Textarea } from '@/components/common';
import {
  FormPageSize,
  FormPhotoResolution,
  FormTextDirection,
  FormThumbSize,
  useUpdateReportLayoutMutation,
  type UpdateReportLayoutPayload,
} from '@/api/endpoints/formsApi';
import type { FormSettingsTabHandle } from './FormSettingsDrawer';

interface FormSettingsReportLayoutProps {
  /** The form being edited (PATCH target). */
  formId: string;
  /** Seed the settings from the form detail (the tab remounts each open). */
  initial?: UpdateReportLayoutPayload | null;
}

// Thumbnail-size cards (title + images-per-row count for the mini preview).
const THUMBS = [
  { key: FormThumbSize.Small, count: 6 },
  { key: FormThumbSize.Medium, count: 4 },
  { key: FormThumbSize.Large, count: 3 },
];

// Report toggle rows, in display order, with their default on/off state.
const TOGGLES = [
  { key: 'showLogo', label: 'form_report_show_logo', on: true },
  { key: 'showQrCode', label: 'form_report_show_qr', on: true },
  { key: 'mediaSummary', label: 'form_report_media_summary', on: false },
  { key: 'workflowSummary', label: 'form_report_workflow_summary', on: false },
  { key: 'showUnanswered', label: 'form_report_show_unanswered', on: false },
  { key: 'hideNumbering', label: 'form_report_hide_numbering', on: false },
  { key: 'footer', label: 'form_report_footer', on: false },
] as const;

/** Footer-text cap, mirrored by the counter under the field. */
const FOOTER_MAX = 240;

const FormSettingsReportLayout = forwardRef<FormSettingsTabHandle, FormSettingsReportLayoutProps>(
  function FormSettingsReportLayout({ formId, initial }, ref) {
    const { t } = useTranslation();
    const [direction, setDirection] = useState<FormTextDirection>(
      initial?.textDirection ?? FormTextDirection.LeftToRight,
    );
    const [pageSize, setPageSize] = useState<FormPageSize>(initial?.pageSize ?? FormPageSize.A4);
    const [thumb, setThumb] = useState<FormThumbSize>(initial?.thumbSize ?? FormThumbSize.Small);
    const [resolution, setResolution] = useState<FormPhotoResolution>(
      initial?.photoResolution ?? FormPhotoResolution.Standard,
    );
    const [toggles, setToggles] = useState<Record<string, boolean>>(() =>
      initial
        ? {
            showLogo: initial.showLogo,
            showQrCode: initial.showQRCode,
            mediaSummary: initial.mediaSummary,
            workflowSummary: initial.workflowSummary,
            showUnanswered: initial.showUnansweredQuestions,
            hideNumbering: initial.hideQuestionNumbering,
            footer: initial.enableFooter,
          }
        : Object.fromEntries(TOGGLES.map((tg) => [tg.key, tg.on])),
    );
    const [footerText, setFooterText] = useState(initial?.footerText ?? '');
    const [updateReportLayout] = useUpdateReportLayoutMutation();

    const flip = (key: string) => setToggles((prev) => ({ ...prev, [key]: !prev[key] }));

    // Persist the Report-layout tab. No client validation — always saves.
    useImperativeHandle(ref, () => ({
      save: async () => {
        await updateReportLayout({
          id: formId,
          body: {
            textDirection: direction,
            showLogo: toggles.showLogo,
            showQRCode: toggles.showQrCode,
            pageSize,
            thumbSize: thumb,
            photoResolution: resolution,
            mediaSummary: toggles.mediaSummary,
            workflowSummary: toggles.workflowSummary,
            showUnansweredQuestions: toggles.showUnanswered,
            hideQuestionNumbering: toggles.hideNumbering,
            enableFooter: toggles.footer,
            footerText,
          },
        }).unwrap();
        return true;
      },
    }));

    return (
      <div className='form-report'>
        <div className='form-report__row'>
          <span className='form-report__row-label'>{t('form_report_text_direction')}</span>
          <SelectDropdown
            className='form-report__row-control'
            aria-label={t('form_report_text_direction')}
            value={direction}
            onChange={(next) => setDirection(next)}
            options={[
              { value: FormTextDirection.LeftToRight, label: t('form_report_dir_ltr') },
              { value: FormTextDirection.RightToLeft, label: t('form_report_dir_rtl') },
            ]}
          />
        </div>

        <div className='form-report__row'>
          <span className='form-report__row-label'>{t('form_report_page_size')}</span>
          <SelectDropdown
            className='form-report__row-control'
            iconStart='file'
            aria-label={t('form_report_page_size')}
            value={pageSize}
            onChange={(next) => setPageSize(next)}
            options={[
              { value: FormPageSize.A4, label: t('form_report_page_a4') },
              { value: FormPageSize.Letter, label: t('form_report_page_letter') },
              { value: FormPageSize.Legal, label: t('form_report_page_legal') },
            ]}
          />
        </div>

        <div className='form-report__field'>
          <span className='form-report__label'>{t('form_report_thumbnail')}</span>
          <div className='form-report__cards'>
            {THUMBS.map((opt) => {
              const selected = thumb === opt.key;
              return (
                <button
                  key={opt.key}
                  type='button'
                  className={`form-report__card${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => setThumb(opt.key)}
                >
                  <span
                    className={`form-report__card-check${selected ? ' is-selected' : ''}`}
                    aria-hidden='true'
                  >
                    {selected && <Icon name='check' size={13} />}
                  </span>
                  <span className='form-report__thumbs'>
                    {Array.from({ length: opt.count }, (_, i) => (
                      <span key={i} className='form-report__thumb' />
                    ))}
                  </span>
                  <span className='form-report__card-title'>
                    {t(`form_report_thumb_${opt.key}`)}
                  </span>
                  <span className='form-report__card-sub'>
                    {t('form_report_thumb_sub', { count: opt.count })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className='form-report__row'>
          <span className='form-report__row-label'>{t('form_report_photo_res')}</span>
          <SelectDropdown
            className='form-report__row-control'
            iconStart='image'
            aria-label={t('form_report_photo_res')}
            value={resolution}
            onChange={(next) => setResolution(next)}
            options={[
              { value: FormPhotoResolution.Standard, label: t('form_report_res_standard') },
              { value: FormPhotoResolution.High, label: t('form_report_res_high') },
            ]}
          />
        </div>

        <hr className='form-report__divider' />

        <div className='form-report__toggles'>
          {TOGGLES.map((tg) => (
            <Fragment key={tg.key}>
              <div className='form-report__toggle'>
                <span className='form-report__toggle-label'>{t(tg.label)}</span>
                <Switch
                  checked={toggles[tg.key]}
                  onChange={() => flip(tg.key)}
                  aria-label={t(tg.label)}
                />
              </div>

              {/* The footer field belongs to its toggle, so it renders right
               * after that row rather than after the whole list. */}
              {tg.key === 'footer' && toggles.footer && (
                <div className='form-report__footer-text'>
                  {/* No visible label per design — the label copy is the a11y name. */}
                  <Textarea
                    rows={3}
                    maxLength={FOOTER_MAX}
                    value={footerText}
                    aria-label={t('form_report_footer_text')}
                    placeholder={t('form_report_footer_text_ph')}
                    onChange={(e) => setFooterText(e.target.value)}
                  />
                  <div className='form-report__footer-meta'>
                    <span className='form-report__footer-hint'>{t('form_report_footer_hint')}</span>
                    {/* Bare counts — data, not copy, so rendered literally.
                     * maxLength blocks typing but never truncates a seeded
                     * value, so a footer saved before the cap existed reads
                     * honestly (e.g. 300/240) rather than being silently
                     * trimmed. */}
                    <span className='form-report__footer-count'>
                      {footerText.length}/{FOOTER_MAX}
                    </span>
                  </div>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    );
  },
);

export default FormSettingsReportLayout;
